import { useCallback, useEffect } from 'react';

import { Form, Select } from 'antd';
import classNames from 'classnames';

import { MODAL_WIDTHS } from '@/shared/lib/constants';
import { BaseModal } from '@/shared/ui/BaseModal';
import { Switch } from '@/shared/ui/Switch';
import { TextInput } from '@/shared/ui/TextInput/TextInput';

import {
  type Connector,
  type ConnectorConfig,
  normalizeConnectorConfigByFields,
  type UpdateConnectorRequest,
  useConnector,
  validateConfigWithFieldRules,
} from '@/entities/connector';

import {
  hasIncompleteRequiredConfig,
  isConnectorDraftEqual,
  isConnectorNameEmpty,
} from '@/features/connectors/lib/connectorFormState';
import { useConfigJsonBridge, useConnectorForm, useUpdateConnector } from '@/features/connectors/model';
import { ASSET_URI_FIELDS } from '@/features/connectors/model/useAssetURIFieldsReset';
import sharedStyles from '@/features/connectors/ui/_internal/connectorForm.module.css';
import { ConfigJsonView } from '@/features/connectors/ui/ConfigJsonView/ConfigJsonView';
import { ResetModal } from '@/features/connectors/ui/ResetModal/ResetModal';

import { ConfigFieldSection } from './_internal/ConfigFieldSection/ConfigFieldSection';
import { EditConnectorDataInfoSection } from './_internal/EditConnectorDataInfoSection';
import { useTypeChangeModal } from './_internal/useTypeChangeModal';
import styles from './EditConnectorModal.module.css';

type EditConnectorModalProps = {
  open: boolean;
  connector: Connector;
  onClose: () => void;
};

const EDIT_CONNECTOR_FORM_ID_PREFIX = 'edit-connector-form';
const JSON_VIEW_SWITCH_LABEL = 'JSON으로 설정 보기/편집';
const NAME_RULES = [
  { required: true, message: '이름을 입력해주세요' },
  { max: 50, message: '최대 50자까지 입력 가능합니다' },
];
const TYPE_RULES = [{ required: true, message: '커넥터 타입을 선택해주세요' }];
const ALL_DATA_INFO_FIELDS = [...ASSET_URI_FIELDS.dataInfo, ...ASSET_URI_FIELDS.dataName];
const TYPE_CHANGE_DESCRIPTION = `Data Source Type을 변경하면\nConfig에 입력한 내용이 모두 삭제됩니다. 계속하시겠습니까?`;

const EMPTY_DATA_INFO: UpdateConnectorRequest['dataInfo'] = {
  enterprise: '',
  site: '',
  area: '',
  process: '',
  unit: '',
  component: '',
  dataName: '',
};

export const EditConnectorModal = ({ open, connector, onClose }: EditConnectorModalProps) => {
  const { data: connectorData } = useConnector({ id: connector.id });
  const originalConnector = connectorData ?? connector;

  const {
    form,
    selectedType,
    showConfigJson,
    setShowConfigJson,
    connectorTypeOptions,
    schemaFields,
    isSchemaLoading,
    isSchemaError,
  } = useConnectorForm();

  const {
    watchedFormConfig,
    jsonDisplayConfig,
    jsonUnmappedConfig,
    hasJsonError,
    handleConfigChange,
    handlePartialConfigChange,
    handleJsonErrorChange,
    resetJsonBridge,
  } = useConfigJsonBridge({
    form,
    schemaFields,
    selectedType,
    initialConfig: originalConnector.config,
  });

  const { mutate: updateConnector, isPending: isUpdatePending } = useUpdateConnector();

  const typeChangeModal = useTypeChangeModal({
    form,
    selectedType,
    resetJsonBridge,
    setShowConfigJson,
  });

  const name = Form.useWatch('name', form);
  const watchedDataInfo = Form.useWatch('dataInfo', form);
  const hasJsonOverrides = Object.keys(jsonUnmappedConfig).length > 0;

  const currentConfig = normalizeConnectorConfigByFields(
    schemaFields,
    watchedFormConfig === undefined && !hasJsonOverrides
      ? (originalConnector.config ?? {})
      : { ...(watchedFormConfig ?? {}), ...jsonUnmappedConfig }
  );
  const currentDataInfo = watchedDataInfo ?? originalConnector.dataInfo;
  const hasChanges = !isConnectorDraftEqual(
    { name: name ?? originalConnector.name, config: currentConfig, dataInfo: currentDataInfo },
    {
      name: originalConnector.name,
      config: originalConnector.config,
      dataInfo: originalConnector.dataInfo,
    }
  );

  const hasInvalidDataInfo = !validateConfigWithFieldRules(currentDataInfo ?? {}, ALL_DATA_INFO_FIELDS).ok;

  const hasIncompleteConfig =
    !!selectedType && !isSchemaLoading && !isSchemaError && hasIncompleteRequiredConfig(schemaFields, currentConfig);
  const hasInvalidConfig =
    !!selectedType &&
    !isSchemaLoading &&
    !isSchemaError &&
    !validateConfigWithFieldRules(currentConfig, schemaFields).ok;

  const isSubmitDisabled =
    isSchemaLoading ||
    isSchemaError ||
    isUpdatePending ||
    (showConfigJson && hasJsonError) ||
    isConnectorNameEmpty(name) ||
    hasIncompleteConfig ||
    hasInvalidConfig ||
    hasInvalidDataInfo ||
    !hasChanges;

  const resetToOriginal = useCallback(() => {
    const originalConfig: ConnectorConfig = { ...(originalConnector.config ?? {}) };

    form.setFieldsValue({
      name: originalConnector.name,
      type: originalConnector.type,
      dataInfo: originalConnector.dataInfo,
    });
    form.setFieldValue('config', originalConfig);
    resetJsonBridge(originalConfig);
    form.setFields(
      Object.keys(originalConnector.dataInfo ?? EMPTY_DATA_INFO).map((fieldKey) => ({
        name: ['dataInfo', fieldKey],
        errors: [],
        warnings: [],
      }))
    );
  }, [
    form,
    originalConnector.config,
    originalConnector.dataInfo,
    originalConnector.name,
    originalConnector.type,
    resetJsonBridge,
  ]);

  const handleCloseModal = useCallback(() => {
    resetToOriginal();
    onClose();
  }, [resetToOriginal, onClose]);

  const handleResetToOriginal = useCallback(() => {
    resetToOriginal();
  }, [resetToOriginal]);

  const handleSubmit = useCallback(
    (values: UpdateConnectorRequest) => {
      const mergedConfig = normalizeConnectorConfigByFields(schemaFields, {
        ...(values.config ?? {}),
        ...jsonUnmappedConfig,
      });

      updateConnector(
        {
          id: connector.id,
          request: { name: values.name, type: values.type ?? '', config: mergedConfig, dataInfo: values.dataInfo },
        },
        { onSuccess: () => handleCloseModal() }
      );
    },
    [connector.id, handleCloseModal, jsonUnmappedConfig, schemaFields, updateConnector]
  );

  useEffect(() => {
    resetToOriginal();
  }, [resetToOriginal]);

  return (
    <BaseModal
      width={MODAL_WIDTHS.STANDARD_FORM}
      title="커넥터 수정"
      open={open}
      onCloseIcon={handleCloseModal}
      onClose={handleResetToOriginal}
      onOk={() => form.submit()}
      okText="수정"
      cancelText="초기화"
      confirmLoading={isUpdatePending}
      cancelButtonProps={{ disabled: isUpdatePending }}
      okButtonProps={{ disabled: isSubmitDisabled, loading: isUpdatePending }}>
      <Form
        name={EDIT_CONNECTOR_FORM_ID_PREFIX}
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className={classNames(sharedStyles.form, styles.form)}>
        <div className={styles.basicInfoGroup}>
          <Form.Item name="name" label="Connector Name" rules={NAME_RULES} className={styles.basicField}>
            <TextInput
              className={styles.basicTextInput}
              placeholder="커넥터 이름 입력"
              disabled={isUpdatePending}
              maxLength={50}
              aria-label="Connector Name"
            />
          </Form.Item>

          <Form.Item name="type" label="Data Source Type" rules={TYPE_RULES} className={styles.basicField}>
            <Select
              placeholder="커넥터 타입 선택"
              options={connectorTypeOptions}
              disabled={true}
              aria-label="Data Source Type"
            />
          </Form.Item>
        </div>

        <EditConnectorDataInfoSection disabled={isUpdatePending} dataInfo={connectorData?.dataInfo} />

        <div className={styles.configSection}>
          <div className={styles.configJsonSection}>
            <h2 className={styles.sectionTitle}>연결 설정</h2>
            <div className={styles.switchWrapper}>
              <label
                id={`${EDIT_CONNECTOR_FORM_ID_PREFIX}-config-json-switch-label`}
                htmlFor={`${EDIT_CONNECTOR_FORM_ID_PREFIX}-config-json-switch`}
                className={styles.switchLabel}>
                {JSON_VIEW_SWITCH_LABEL}
              </label>
              <Switch
                id={`${EDIT_CONNECTOR_FORM_ID_PREFIX}-config-json-switch`}
                checked={showConfigJson}
                onChange={setShowConfigJson}
                aria-labelledby={`${EDIT_CONNECTOR_FORM_ID_PREFIX}-config-json-switch-label`}
                color="standard"
              />
            </div>

            <ConfigJsonView
              idPrefix={EDIT_CONNECTOR_FORM_ID_PREFIX}
              value={jsonDisplayConfig}
              fields={schemaFields}
              onChange={(config, invalidMappedKeys) => {
                if (invalidMappedKeys?.length) {
                  handlePartialConfigChange({ config, invalidMappedKeys });
                  return;
                }

                handleConfigChange(config);
              }}
              onErrorChange={handleJsonErrorChange}
              disabled={isUpdatePending}
              showConfigJson={showConfigJson}
            />
            <ConfigFieldSection
              schemaFields={schemaFields}
              disabled={isUpdatePending}
              showConfigJson={showConfigJson}
            />
          </div>
        </div>

        <ResetModal
          open={typeChangeModal.open}
          description={TYPE_CHANGE_DESCRIPTION}
          onConfirm={typeChangeModal.handleConfirm}
          onCancel={typeChangeModal.handleCancel}
        />
      </Form>
    </BaseModal>
  );
};
